// src/App.js
import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { useEffect } from 'react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './components/Home';
import AlumniDirectory from './components/AlumniDirectory';
import AlumniDetail from './components/AlumniDetail';
import AddAlumni from './components/AddAlumni';
import EditAlumni from './components/EditAlumni';
import Statistics from './components/Statistics';
import AllCareers from './components/AllCareers';
import NewsPage from './components/NewsPage';
import NewsDetail from './components/NewsDetail';
import About from './components/About';
import Login from './components/Login';
import RequestAlumni from './components/RequestAlumni';
import AdminRequests from './components/AdminRequests';
import AdminNews from './components/AdminNews';
import NewsEditor from './components/NewsEditor';
import ProjectsPage from './components/ProjectsPage';
import AdminProjects from './components/AdminProjects';
import NotificationSettings from './components/NotificationSettings';
import AdminQuiz from './components/AdminQuiz';
import RequestEditAlumni from './components/RequestEditAlumni';
import AdminEditRequests from './components/AdminEditRequests';
import PkmBank from './components/PkmBank';
import AdminPkmBank from './components/AdminPkmBank';
import PkmFeatures from './components/PkmFeatures';
import PkmBuilder from './components/PkmBuilder';
import QuizPage from './components/QuizPage';
import GuessPage from './components/GuessPage';
import CatchMePage from './components/CatchMePage';

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Protected route wrapper — redirects to login if not admin
const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div></div>;
  return isAdmin ? children : <Navigate to="/login" />;
};

// Layout with Navbar + Footer for regular pages
const MainLayout = () => (
  <div className="flex flex-col min-h-screen font-inter bg-gray-50 dark:bg-dark-900 transition-colors duration-300">
    <Navbar />
    <main className="flex-1">
      <Outlet />
    </main>
    <Footer />
  </div>
);

const App = () => {
  return (
    <ThemeProvider>
    <LanguageProvider>
    <Router>
      <AuthProvider>
        <ScrollToTop />
        <Routes>
          {/* Standalone game pages — no Navbar/Footer (open in new tab) */}
          <Route path="/trivia/kuis" element={<QuizPage />} />
          <Route path="/trivia/tebak-alumni" element={<GuessPage />} />
          <Route path="/trivia/catch-me" element={<CatchMePage />} />

          {/* All other pages with Navbar + Footer */}
          <Route element={<MainLayout />}>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/alumni" element={<AlumniDirectory />} />
            <Route path="/alumni/:id" element={<AlumniDetail />} />
            <Route path="/statistik" element={<Statistics />} />
            <Route path="/statistik/karir" element={<AllCareers />} />
            <Route path="/berita" element={<NewsPage />} />
            <Route path="/berita/:slug" element={<NewsDetail />} />
            <Route path="/proyek" element={<ProjectsPage />} />
            <Route path="/tentang" element={<About />} />
            <Route path="/login" element={<Login />} />
            <Route path="/ajukan-alumni" element={<RequestAlumni />} />
            <Route path="/alumni/:id/request-edit" element={<RequestEditAlumni />} />
            <Route path="/pkm-bank" element={<PkmBank />} />
            <Route path="/pkm-corner" element={<PkmFeatures />} />
            <Route path="/pkm-builder" element={<PkmBuilder />} />

            {/* Admin-only routes */}
            <Route path="/add-alumni" element={<AdminRoute><AddAlumni /></AdminRoute>} />
            <Route path="/edit-alumni/:id" element={<AdminRoute><EditAlumni /></AdminRoute>} />
            <Route path="/admin/requests" element={<AdminRoute><AdminRequests /></AdminRoute>} />
            <Route path="/admin/news" element={<AdminRoute><AdminNews /></AdminRoute>} />
            <Route path="/admin/news/new" element={<AdminRoute><NewsEditor /></AdminRoute>} />
            <Route path="/admin/news/edit/:id" element={<AdminRoute><NewsEditor /></AdminRoute>} />
            <Route path="/admin/projects" element={<AdminRoute><AdminProjects /></AdminRoute>} />
            <Route path="/admin/notifications" element={<AdminRoute><NotificationSettings /></AdminRoute>} />
            <Route path="/admin/quiz" element={<AdminRoute><AdminQuiz /></AdminRoute>} />
            <Route path="/admin/edit-requests" element={<AdminRoute><AdminEditRequests /></AdminRoute>} />
            <Route path="/admin/pkm-bank" element={<AdminRoute><AdminPkmBank /></AdminRoute>} />
          </Route>
        </Routes>
      </AuthProvider>
    </Router>
    </LanguageProvider>
    </ThemeProvider>
  );
};

export default App;
